import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  toast,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckSquare,
  Eye,
  HelpCircle,
  Info,
  Save,
  Send,
  Sparkles,
  Workflow,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { QueryState } from "@/shared/ui/QueryState";
import type { FormDTO } from "../../domain/contracts";
import { formsApi } from "../api/client";
import {
  useCreateForm,
  useFormFieldsQuery,
  useFormQuery,
  useFormVersionsQuery,
  useUpdateForm,
} from "../api/queries";
import { FormBuilderTour, useFormBuilderTour } from "../tour/FormBuilderTour";
import type { FormField, FormFieldType } from "../types/form-builder";
import { FieldPropertiesPanel } from "../ui/FieldPropertiesPanel";
import { FieldTypesPanel } from "../ui/FieldTypesPanel";
import { FormEditorPanel } from "../ui/FormEditorPanel";
import { FormMetadataSection } from "../ui/FormMetadataSection";
import { FormVersioningPanel } from "../ui/FormVersioningPanel";
import { formBuilderSamples } from "../ui/formBuilderSamples";

export function FormBuilderPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const nav = useNavigate();
  const isEdit = !!params?.id;
  const formId = params?.id ?? "";

  // Fetch form data in edit mode
  const formQuery = useFormQuery(formId);
  const form = formQuery.data as FormDTO | undefined;

  // Fetch fields and versions in edit mode
  const { data: fieldsData } = useFormFieldsQuery(
    formId,
    form?.currentVersionId ?? null,
  );
  const { data: versionsData } = useFormVersionsQuery(formId);

  // Mutations
  const createFormMutation = useCreateForm();
  const updateFormMutation = useUpdateForm(isEdit ? formId : "");
  const queryClient = useQueryClient();
  const tour = useFormBuilderTour();

  // Form metadata state
  const [formName, setFormName] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [useCase, setUseCase] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [processAfterSubmission, setProcessAfterSubmission] = useState("");
  const [allowFileAttachment, setAllowFileAttachment] = useState(false);

  // Form fields state
  const [fields, setFields] = useState<FormField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  // Sample data index for auto-fill
  const [sampleIndex, setSampleIndex] = useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  // Version state
  const [versions, setVersions] = useState<
    Array<{
      id: string;
      version: string;
      changelog: string;
      isCurrent: boolean;
      publishedAt: string | null;
    }>
  >([]);

  // Load form data when in edit mode
  useEffect(() => {
    if (isEdit && form) {
      setFormName(form.name);
      setCategoryId(form.categoryId);
      setDepartmentId(form.departmentId);
      setDescription(form.description ?? "");
      setUseCase(form.useCase ?? "");
      setTargetAudience(form.targetAudience ?? "");
      setProcessAfterSubmission(form.processAfterSubmission ?? "");
      setAllowFileAttachment(form.allowFileAttachment);
    }
  }, [isEdit, form]);

  // Load form fields from current version
  useEffect(() => {
    if (fieldsData?.fields && Array.isArray(fieldsData.fields)) {
      const fieldsArray = fieldsData.fields as Array<{
        id: string;
        fieldType: string;
        label: string;
        isRequired: boolean;
        hint: string | null;
        orderIndex: number;
        options: unknown;
        settings: Record<string, unknown> | null;
      }>;
      const mappedFields: FormField[] = fieldsArray.map((f) => ({
        id: f.id,
        type: f.fieldType as FormFieldType,
        label: f.label,
        required: f.isRequired,
        placeholder: f.hint ?? "",
        order: f.orderIndex,
        config: {
          options: f.options
            ? Array.isArray(f.options)
              ? f.options
              : typeof f.options === "object"
                ? Object.entries(f.options).map(([value, label]) => ({
                    value,
                    label: String(label),
                  }))
                : []
            : undefined,
          ...(f.settings || {}),
        },
      }));
      setFields(mappedFields);
    }
  }, [fieldsData]);

  // Load versions list
  useEffect(() => {
    if (versionsData?.versions && Array.isArray(versionsData.versions)) {
      const versionsArray = versionsData.versions as Array<{
        id: string;
        version: string;
        changelog: string | null;
        isCurrent: boolean;
        publishedAt: string | null;
      }>;
      const mappedVersions = versionsArray.map((v) => ({
        id: v.id,
        version: v.version,
        changelog: v.changelog ?? "",
        isCurrent: v.isCurrent,
        publishedAt: v.publishedAt,
      }));
      setVersions(mappedVersions);
    }
  }, [versionsData]);

  const selectedField = fields.find((f) => f.id === selectedFieldId);

  const handleAddField = (fieldType: FormFieldType) => {
    const newField: FormField = {
      id: `field-${Date.now()}`,
      type: fieldType,
      label: "",
      required: false,
      placeholder: "",
      order: fields.length,
      config: {},
    };
    setFields([...fields, newField]);
    setSelectedFieldId(newField.id);
  };

  const handleUpdateField = (fieldId: string, updates: Partial<FormField>) => {
    setFields(fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)));
  };

  const handleDeleteField = (fieldId: string) => {
    setFields(fields.filter((f) => f.id !== fieldId));
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
    }
  };

  const handleMoveField = (fieldId: string, direction: "up" | "down") => {
    const index = fields.findIndex((f) => f.id === fieldId);
    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    const newFields = [...fields];
    const fieldAtIndex = newFields[index];
    const fieldAtNewIndex = newFields[newIndex];
    if (fieldAtIndex && fieldAtNewIndex) {
      const updatedFieldAtIndex = { ...fieldAtNewIndex, order: index };
      const updatedFieldAtNewIndex = { ...fieldAtIndex, order: newIndex };
      newFields[index] = updatedFieldAtIndex;
      newFields[newIndex] = updatedFieldAtNewIndex;
      setFields(newFields);
    }
  };

  // Validate form fields
  const validateFields = (): string | null => {
    // Validate that all fields have labels
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field) continue;
      if (!field.label.trim()) {
        return `ກະລຸນາໃສ່ຊື່ຊ່ອງຂໍ້ມູນທີ່ ${i + 1}`;
      }
    }

    // Validate fields that need options
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field) continue;
      if (["select", "multiselect", "radio"].includes(field.type)) {
        if (!field.config.options || field.config.options.length === 0) {
          return `ກະລຸນາເພີ່ມຕົວເລືອກສຳລັບຊ່ອງຂໍ້ມູນ "${field.label || `ຊ່ອງທີ່ ${i + 1}`}"`;
        }
        // Validate that options have both label and value
        const invalidOptions = field.config.options.filter(
          (opt: { label?: string; value?: string }) =>
            !opt.label?.trim() || !opt.value?.trim(),
        );
        if (invalidOptions.length > 0) {
          return `ຕົວເລືອກບາງຕົວບໍ່ຖືກຕ້ອງສຳລັບຊ່ອງຂໍ້ມູນ "${field.label || `ຊ່ອງທີ່ ${i + 1}`}"`;
        }
      }
    }

    // Validate number field constraints
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field) continue;
      if (field.type === "number") {
        if (
          field.config.min !== undefined &&
          field.config.max !== undefined &&
          field.config.min > field.config.max
        ) {
          return `ຄ່າຕ່ຳສຸດຕ້ອງນ້ອຍກວ່າຫຼືເທົ່າກັບຄ່າສູງສຸດສຳລັບຊ່ອງຂໍ້ມູນ "${field.label || `ຊ່ອງທີ່ ${i + 1}`}"`;
        }
      }
    }

    // Validate date field constraints
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field) continue;
      if (field.type === "date" || field.type === "datetime") {
        if (
          field.config.minDate &&
          field.config.maxDate &&
          field.config.minDate > field.config.maxDate
        ) {
          return `ວັນທີ່ເລີ່ມຕົ້ນຕ້ອງນ້ອຍກວ່າຫຼືເທົ່າກັບວັນທີ່ສິ້ນສຸດສຳລັບຊ່ອງຂໍ້ມູນ "${field.label || `ຊ່ອງທີ່ ${i + 1}`}"`;
        }
      }
    }

    return null;
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formName.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ຟອມ");
      return;
    }

    // Validate form fields
    const fieldValidationError = validateFields();
    if (fieldValidationError) {
      toast.error(fieldValidationError);
      return;
    }

    const formData = {
      name: formName,
      categoryId: categoryId ?? null,
      departmentId: departmentId ?? null,
      description: description || null,
      useCase: useCase || null,
      targetAudience: targetAudience || null,
      processAfterSubmission: processAfterSubmission || null,
      allowFileAttachment,
      status: "draft" as const,
    };

    try {
      let savedFormId: string;

      if (isEdit) {
        // Update existing form - include fields so server can create version automatically
        await updateFormMutation.mutateAsync({
          ...formData,
          fields:
            fields.length > 0
              ? fields.map((field) => ({
                  label: field.label,
                  fieldType: field.type,
                  isRequired: field.required,
                  hint: field.placeholder || null,
                  orderIndex: field.order,
                  options: field.config.options || null,
                  settings: field.config || null,
                }))
              : undefined,
        });
        savedFormId = formId;
      } else {
        // Create new form
        const result = (await createFormMutation.mutateAsync(
          formData,
        )) as FormDTO;
        if (!result?.id) {
          toast.error("ບໍ່ສາມາດສ້າງຟອມໄດ້");
          return;
        }
        savedFormId = result.id;

        // Save form version with fields for new forms
        if (fields.length > 0) {
          await formsApi.saveVersion(savedFormId, {
            version: "1.0",
            changelog: "ສ້າງເວີຊັນເບື້ອງຕົ້ນ",
            fields: fields.map((field) => ({
              label: field.label,
              fieldType: field.type,
              isRequired: field.required,
              hint: field.placeholder || null,
              orderIndex: field.order,
              options: field.config.options || null,
              settings: field.config || null,
            })),
            isCurrent: true,
          });
        }
      }

      // Navigate to edit page if it was a new form
      if (!isEdit && savedFormId) {
        nav({
          to: "/app/forms/$id",
          params: { id: savedFormId },
        });
      } else {
        toast.success("ບັນທຶກຟອມສຳເລັດ");
      }
    } catch (error) {
      // Error is already handled by the mutation's onError callback
      console.error("Failed to save form:", error);
      toast.error("ບັນທຶກຟອມລົ້ມເຫຼວ");
    }
  };

  const handlePublish = async () => {
    // Validate required fields
    if (!formName.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ຟອມ");
      return;
    }

    if (fields.length === 0) {
      toast.error("ກະລຸນາເພີ່ມຊ່ອງຂໍ້ມູນຢ່າງໜ້ອຍ 1 ຊ່ອງ");
      return;
    }

    // Validate form fields
    const fieldValidationError = validateFields();
    if (fieldValidationError) {
      toast.error(fieldValidationError);
      return;
    }

    try {
      let savedFormId: string;

      // First, save the form (create or update)
      if (isEdit) {
        // Update with fields - server will create version automatically
        await updateFormMutation.mutateAsync({
          name: formName,
          categoryId: categoryId ?? null,
          departmentId: departmentId ?? null,
          description: description || null,
          useCase: useCase || null,
          targetAudience: targetAudience || null,
          processAfterSubmission: processAfterSubmission || null,
          allowFileAttachment,
          status: "active", // Set status to active when publishing
          fields:
            fields.length > 0
              ? fields.map((field) => ({
                  label: field.label,
                  fieldType: field.type,
                  isRequired: field.required,
                  hint: field.placeholder || null,
                  orderIndex: field.order,
                  options: field.config.options || null,
                  settings: field.config || null,
                }))
              : undefined,
        });
        savedFormId = formId;
      } else {
        const result = (await createFormMutation.mutateAsync({
          name: formName,
          categoryId: categoryId ?? null,
          departmentId: departmentId ?? null,
          description: description || null,
          useCase: useCase || null,
          targetAudience: targetAudience || null,
          processAfterSubmission: processAfterSubmission || null,
          allowFileAttachment,
          status: "active", // Set status to active when publishing
        })) as FormDTO;
        if (!result?.id) {
          toast.error("ບໍ່ສາມາດສ້າງຟອມໄດ້");
          return;
        }
        savedFormId = result.id;

        // Save form version with fields for new forms
        if (fields.length > 0) {
          await formsApi.saveVersion(savedFormId, {
            version: "1.0",
            changelog: "ເຜີຍແຜ່ຟອມເບື້ອງຕົ້ນ",
            fields: fields.map((field) => ({
              label: field.label,
              fieldType: field.type,
              isRequired: field.required,
              hint: field.placeholder || null,
              orderIndex: field.order,
              options: field.config.options || null,
              settings: field.config || null,
            })),
            isCurrent: true,
          });
        }
      }

      toast.success("ເຜີຍແຜ່ຟອມສຳເລັດ");

      // Navigate to form detail page
      if (!isEdit && savedFormId) {
        nav({
          to: "/app/forms/$id",
          params: { id: savedFormId },
        });
      } else if (isEdit) {
        // Refresh form data to show updated status
        queryClient.invalidateQueries({
          queryKey: ["forms", "detail", formId],
        });
      }
    } catch (error) {
      console.error("Failed to publish form:", error);
      toast.error("ເຜີຍແຜ່ຟອມລົ້ມເຫຼວ");
    }
  };

  // Handle auto-fill with sample data
  const handleAutoFill = () => {
    if (isEdit) {
      toast.error("ບໍ່ສາມາດໃຊ້ຕົວຢ່າງໃນໂໝດແກ້ໄຂ");
      return;
    }

    const sample = formBuilderSamples[sampleIndex];
    if (sample) {
      const currentSampleNumber = sampleIndex + 1;

      setFormName(sample.name);
      setCategoryId(sample.categoryId ?? null);
      setDepartmentId(sample.departmentId ?? null);
      setDescription(sample.description);
      setUseCase(sample.useCase);
      setTargetAudience(sample.targetAudience);
      setProcessAfterSubmission(sample.processAfterSubmission);
      setAllowFileAttachment(sample.allowFileAttachment);

      // Convert sample fields to FormField format with IDs
      const sampleFields: FormField[] = sample.fields.map((field, idx) => ({
        id: `field-${Date.now()}-${idx}`,
        type: field.type,
        label: field.label,
        required: field.required,
        placeholder: field.placeholder,
        order: field.order,
        config: field.config,
      }));
      setFields(sampleFields);

      // Cycle to next sample
      setSampleIndex((prev) => (prev + 1) % formBuilderSamples.length);
      toast.success(
        `ຕື່ມຂໍ້ມູນຕົວຢ່າງສຳເລັດ (${currentSampleNumber}/${formBuilderSamples.length}). ກະລຸນາຕື່ມຂໍ້ມູນທີ່ຈໍາເປັນເພີ່ມເຕີມ`,
      );
    }
  };

  return (
    <>
      <Header />
      <Main>
        <FormBuilderTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />

        <QueryState
          result={isEdit ? formQuery : undefined}
          variant="fullscreen"
          isEmpty={isEdit && !form}
          emptyFallback={
            <div className="text-center">
              <p className="text-muted-foreground">ບໍ່ພົບຟອມ</p>
            </div>
          }
        >
          <div className="space-y-6">
            {/* Back Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (isEdit && form) {
                  nav({ to: "/app/forms/$id", params: { id: form.id } });
                } else {
                  nav({
                    to: "/app/forms",
                    search: {
                      offset: 0,
                      limit: 20,
                      sort: undefined as
                        | Array<{ field: string; dir: "asc" | "desc" }>
                        | undefined,
                      filters: undefined as
                        | Array<{ field: string; op: string; value: unknown }>
                        | undefined,
                    },
                  });
                }
              }}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              ກັບຄືນ
            </Button>

            {/* Form Header */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="font-bold text-3xl">
                    {isEdit && form ? form.name : isEdit ? "ແກ້ໄຂຟອມ" : "ສ້າງຟອມ"}
                  </h1>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 rounded-full"
                    onClick={tour.startTour}
                  >
                    <HelpCircle className="size-5" />
                  </Button>
                  {isEdit && form && (
                    <Badge
                      variant={form.status === "active" ? "default" : "outline"}
                    >
                      {form.status === "active"
                        ? "ໃຊ້ງານ"
                        : form.status === "draft"
                          ? "ຮ່າງ"
                          : form.status}
                    </Badge>
                  )}
                </div>
                {isEdit && form && (
                  <div className="mt-2 flex items-center gap-4 text-muted-foreground text-sm">
                    <span>ລະຫັດຟອມ: {form.id}</span>
                    {form.categoryName && (
                      <>
                        <span>•</span>
                        <span>ໝວດໝູ່: {form.categoryName}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>ສ້າງເມື່ອ: {formatDateLocal(form.createdAt)}</span>
                  </div>
                )}
              </div>
              <div
                className="flex items-center gap-2"
                data-tourid="form-actions"
              >
                {isDev && !isEdit && (
                  <Button variant="outline" size="sm" onClick={handleAutoFill}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    ຕົວຢ່າງ ({sampleIndex + 1}/{formBuilderSamples.length})
                  </Button>
                )}
                {fields.length > 0 && isEdit && formId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      nav({
                        to: "/app/forms/$id/preview",
                        params: { id: formId },
                      })
                    }
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    ເບິ່ງຕົວຢ່າງ
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSave}
                  disabled={
                    createFormMutation.isPending || updateFormMutation.isPending
                  }
                >
                  <Save className="mr-2 h-4 w-4" />
                  {createFormMutation.isPending || updateFormMutation.isPending
                    ? "ກຳລັງບັນທຶກ..."
                    : "ບັນທຶກ"}
                </Button>
                <Button size="sm" onClick={handlePublish}>
                  <Send className="mr-2 h-4 w-4" />
                  ເຜີຍແຜ່
                </Button>
              </div>
            </div>

            {/* Main Content — full width (ໂຄງສ້າງຟອມ ແລະ ການແກ້ໄຂເຕັມໜ້າຈໍ) */}
            <div className="space-y-6">
              {/* Form Metadata Card */}
              <Card>
                <CardHeader>
                  <CardTitle>ຂໍ້ມູນຟອມ</CardTitle>
                </CardHeader>
                <CardContent>
                  <FormMetadataSection
                    formName={formName}
                    setFormName={setFormName}
                    categoryId={categoryId}
                    setCategoryId={setCategoryId}
                    departmentId={departmentId}
                    setDepartmentId={setDepartmentId}
                    description={description}
                    setDescription={setDescription}
                    useCase={useCase}
                    setUseCase={setUseCase}
                    targetAudience={targetAudience}
                    setTargetAudience={setTargetAudience}
                    processAfterSubmission={processAfterSubmission}
                    setProcessAfterSubmission={setProcessAfterSubmission}
                    allowFileAttachment={allowFileAttachment}
                    setAllowFileAttachment={setAllowFileAttachment}
                  />
                </CardContent>
              </Card>

              {/* Form Instructions Card */}
              {(description ||
                useCase ||
                targetAudience ||
                processAfterSubmission) && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Info className="h-5 w-5" />
                      <CardTitle>ຄຳແນະນຳແລະຈຸດປະສົງຂອງຟອມ</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {description && (
                      <div>
                        <h3 className="mb-2 font-semibold">ບໍລິບົດການນຳໃຊ້</h3>
                        <p className="text-muted-foreground text-sm">
                          {description}
                        </p>
                      </div>
                    )}

                    {useCase && (
                      <div>
                        <div className="mb-2 flex items-center gap-2">
                          <CheckSquare className="h-4 w-4" />
                          <h3 className="font-semibold">ໃຊ້ກັບກໍລະນີໃດ</h3>
                        </div>
                        <p className="ml-6 text-muted-foreground text-sm">
                          {useCase}
                        </p>
                      </div>
                    )}

                    {processAfterSubmission && (
                      <div>
                        <div className="mb-2 flex items-center gap-2">
                          <Workflow className="h-4 w-4" />
                          <h3 className="font-semibold">ຂະບວນການຫຼັງສົ່ງ</h3>
                        </div>
                        <p className="ml-6 text-muted-foreground text-sm">
                          {processAfterSubmission}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Form Builder - Three Panel Layout */}
              <Card>
                <CardHeader>
                  <CardTitle>ໂຄງສ້າງຟອມ</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-12 gap-4">
                    {/* Left Panel: Field Types */}
                    <div className="col-span-3">
                      <FieldTypesPanel onAddField={handleAddField} />
                    </div>

                    {/* Center Panel: Form Editor */}
                    <div className="col-span-6">
                      <FormEditorPanel
                        fields={fields}
                        selectedFieldId={selectedFieldId}
                        onSelectField={setSelectedFieldId}
                        onDeleteField={handleDeleteField}
                        onMoveField={handleMoveField}
                      />
                    </div>

                    {/* Right Panel: Field Properties */}
                    <div className="col-span-3">
                      {selectedField ? (
                        <FieldPropertiesPanel
                          field={selectedField}
                          onUpdateField={handleUpdateField}
                        />
                      ) : (
                        <div className="rounded-xl border bg-card p-4 text-center text-muted-foreground">
                          ເລືອກຊ່ອງຂໍ້ມູນເພື່ອແກ້ໄຂຄຸນສົມບັດ
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Metadata & versions — ຢູ່ລຸ່ມ ໂຄງສ້າງຟອມ (ເຕັມຄວາມກວ້າງ) */}
              {isEdit && form && (
                <Card>
                  <CardHeader>
                    <CardTitle>ຂໍ້ມູນເພີ່ມເຕີມ</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                        <span>ສ້າງໂດຍ</span>
                      </div>
                      <p className="font-medium text-sm">
                        {form.createdByName || form.createdBy}
                      </p>
                    </div>

                    <div>
                      <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                        <span>ອັບເດດລ່າສຸດ</span>
                      </div>
                      <p className="font-medium text-sm">
                        {formatDateTimeLocal(form.updatedAt)}
                      </p>
                    </div>

                    {form.departmentName && (
                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <span>ພະແນກ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {form.departmentName}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {isEdit && versions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>ເວີຊັນ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <FormVersioningPanel
                      versions={versions}
                      onAddVersion={async (version, changelog) => {
                        if (!formId || !version.trim()) {
                          toast.error("ກະລຸນາໃສ່ເວີຊັນ");
                          return;
                        }

                        if (fields.length === 0) {
                          toast.error("ກະລຸນາເພີ່ມຊ່ອງຂໍ້ມູນກ່ອນສ້າງເວີຊັນ");
                          return;
                        }

                        try {
                          await formsApi.saveVersion(formId, {
                            version: version.trim(),
                            changelog: changelog || null,
                            fields: fields.map((field) => ({
                              label: field.label,
                              fieldType: field.type,
                              isRequired: field.required,
                              hint: field.placeholder || null,
                              orderIndex: field.order,
                              options: field.config.options || null,
                              settings: field.config || null,
                            })),
                            isCurrent: false,
                          });

                          toast.success("ສ້າງເວີຊັນສຳເລັດ");

                          queryClient.invalidateQueries({
                            queryKey: ["forms", "versions", formId],
                          });
                        } catch (error) {
                          console.error("Failed to add version:", error);
                          toast.error("ສ້າງເວີຊັນລົ້ມເຫຼວ");
                        }
                      }}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </QueryState>
      </Main>
    </>
  );
}
